import { useMemo } from "react";
import { computeMACD, type OHLCV } from "@utils/technicalAnalysisCalc";
import type { OhlcCandlePoint } from "../types";
import { cn } from "utils";

interface MiniMacdChartProps {
  candles: OhlcCandlePoint[];
  className?: string;
}

const BAR_WIDTH = 3.09;
const BAR_GAP = 0.7;
const LINE_STROKE_WIDTH = 0.5;
const CHART_HEIGHT = 60;
const PADDING_TOP = 8;
const PADDING_BOTTOM = 8;
const MACD_DISPLAY_COUNT = 60;

const toOhlcv = (candles: OhlcCandlePoint[]): OHLCV[] =>
  candles.map((candle, index) => ({
    timestamp: index,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
  }));

const backfillFinite = (line: number[]) => {
  let last = 0;
  return line.map((value) => {
    if (Number.isFinite(value)) {
      last = value;
      return value;
    }
    return last;
  });
};

const lastFinite = (line: number[]) => {
  for (let index = line.length - 1; index >= 0; index -= 1) {
    const value = line[index];
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

export const formatMacdDisplay = (value: number) => {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const abs = Math.abs(value);
  if (abs >= 100) {
    return Math.round(value).toLocaleString("en-US");
  }
  if (abs >= 1) {
    return value.toFixed(1);
  }
  return value.toFixed(2);
};

/** 由 OHLC 推导 MACD 柱线、DIF/DEA 与 Y 轴范围 */
export const deriveMacdChartSeries = (candles: OhlcCandlePoint[]) => {
  const { dif, dea, macd } = computeMACD(toOhlcv(candles));
  const displayStart = Math.max(0, candles.length - MACD_DISPLAY_COUNT);

  const histogramValues = backfillFinite(macd.slice(displayStart));
  const difLine = backfillFinite(dif.slice(displayStart));
  const deaLine = backfillFinite(dea.slice(displayStart));

  const chartValues = [...histogramValues, ...difLine, ...deaLine].filter(
    Number.isFinite,
  );
  const rawMax = chartValues.length ? Math.max(...chartValues) : 1;
  const rawMin = chartValues.length ? Math.min(...chartValues) : -1;
  const padding = (rawMax - rawMin) * 0.05 || 0.5;

  return {
    histogramValues,
    difLine,
    deaLine,
    yAxisMax: rawMax + padding,
    yAxisMin: rawMin - padding,
    lastDif: lastFinite(dif),
    lastDea: lastFinite(dea),
    lastMacd: lastFinite(macd),
  };
};

const scaleY = (
  value: number,
  min: number,
  max: number,
  height: number,
  paddingTop: number,
  paddingBottom: number,
) => {
  const range = max - min || 1;
  const innerH = height - paddingTop - paddingBottom;
  return paddingTop + (1 - (value - min) / range) * innerH;
};

const isPositiveBar = (value: number) => value > 0;

/**
 * MACD 柱四色（vkno422 / MT4 / TradingView MACD 4C 惯例）：
 * - 柱 > 0 且 > 前一根 → 深绿（多头动能增强）
 * - 柱 > 0 且 ≤ 前一根 → 浅绿（多头动能减弱）
 * - 柱 ≤ 0 且 < 前一根 → 深红（空头动能增强）
 * - 柱 ≤ 0 且 ≥ 前一根 → 浅红（空头动能减弱）
 * 首根柱以前一根为 0 比较。
 */
const getHistogramBarFill = (value: number, prev: number) => {
  if (value > 0) {
    return value > prev
      ? "fill-content_accent1_primary"
      : "fill-content_accent1_secondary";
  }
  return value < prev
    ? "fill-content_accent2_primary"
    : "fill-content_accent2_secondary";
};

/** 柱宽 3px；同色相邻柱间距 0.7px，红绿衔接处无间距 */
const layoutHistogramBars = (values: number[]) => {
  const barXs: number[] = [];
  const barCenters: number[] = [];
  let cursor = 0;

  values.forEach((value, index) => {
    barXs.push(cursor);
    barCenters.push(cursor + BAR_WIDTH / 2);
    cursor += BAR_WIDTH;

    if (index < values.length - 1) {
      const sameSign =
        isPositiveBar(value) === isPositiveBar(values[index + 1]!);
      if (sameSign) {
        cursor += BAR_GAP;
      }
    }
  });

  return {
    barXs,
    barCenters,
    chartWidth: Math.max(cursor, BAR_WIDTH),
  };
};

/**
 * MACD 柱线 + DIF/DEA 双线。 Figma：94103:23289
 * 外部仅传 OHLC；指标与 Y 轴由 computeMACD(12,26,9) 内部计算。
 */
export const MiniMacdChart: React.FC<MiniMacdChartProps> = ({
  candles,
  className,
}) => {
  const { histogramValues, difLine, deaLine, yAxisMax, yAxisMin } = useMemo(
    () => deriveMacdChartSeries(candles),
    [candles],
  );

  const zeroY = scaleY(
    0,
    yAxisMin,
    yAxisMax,
    CHART_HEIGHT,
    PADDING_TOP,
    PADDING_BOTTOM,
  );

  const { barXs, barCenters, chartWidth } = useMemo(
    () => layoutHistogramBars(histogramValues),
    [histogramValues],
  );

  const toPoints = (values: number[], xs: number[]) =>
    values
      .map((value, index) => {
        const x = xs[index] ?? xs[xs.length - 1] ?? 0;
        const y = scaleY(
          value,
          yAxisMin,
          yAxisMax,
          CHART_HEIGHT,
          PADDING_TOP,
          PADDING_BOTTOM,
        );
        return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");

  return (
    <div className={cn("flex w-full gap-2", className)}>
      <svg
        viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
        className="min-w-0 flex-1"
        style={{ height: CHART_HEIGHT }}
        aria-hidden
      >
        <line
          x1={0}
          y1={zeroY}
          x2={chartWidth}
          y2={zeroY}
          className="stroke-border_white_transparent10"
          strokeWidth={0.5}
        />
        {histogramValues.map((value, index) => {
          const x = barXs[index] ?? 0;
          const prev = index > 0 ? histogramValues[index - 1]! : 0;
          const y = scaleY(
            value,
            yAxisMin,
            yAxisMax,
            CHART_HEIGHT,
            PADDING_TOP,
            PADDING_BOTTOM,
          );
          const barHeight = Math.abs(y - zeroY);
          const isPositive = isPositiveBar(value);
          return (
            <rect
              key={index}
              x={x}
              y={isPositive ? y : zeroY}
              width={BAR_WIDTH}
              height={Math.max(1, barHeight)}
              className={getHistogramBarFill(value, prev)}
            />
          );
        })}
        <path
          d={toPoints(difLine, barCenters)}
          fill="none"
          className="stroke-content_brand_primary"
          strokeWidth={LINE_STROKE_WIDTH}
        />
        <path
          d={toPoints(deaLine, barCenters)}
          fill="none"
          className="stroke-content_accent4_primary"
          strokeWidth={LINE_STROKE_WIDTH}
        />
      </svg>
      <div className="flex w-5 shrink-0 flex-col justify-between body_medium_xs text-content_quaternary">
        <span>{yAxisMax.toFixed(1)}</span>
        <span>{yAxisMin.toFixed(1)}</span>
      </div>
    </div>
  );
};
